// DataTable - the table primitives assembled once. G360_K432
//
// table.jsx and tiles.jsx hold the parts: sort, filter, column picker,
// positional column hiding, CSV export, tiles. Every page so far has
// wired them together by hand, and the wiring is where the mistakes
// were: two columns claiming one position, an identifying column that
// could be hidden, a "no match" row that vanished with the first
// column, an export that did not match the screen. This component does
// the wiring one way, so a page supplies only its columns.
//
// columns: [{
//   key      unique on the table
//   label    header text
//   value    (row) => the underlying value. Used for filtering, and for
//            sorting and export unless sort / csv say otherwise.
//            Defaults to row[key].
//   sort     (row) => comparable, or null for "nothing here" (sorts last)
//   csv      (row) => what the export receives
//   render   (row) => what the cell shows. Defaults to value.
//   align    'right' for numbers
//   title    header tooltip
// }]
//
// Rules this enforces rather than asks for:
//   - positions are the column order, 1..N, so they cannot collide or gap
//   - the first column identifies the row and is never offered for
//     hiding, so the "no match" row (which sits in the first cell) and
//     any expanded row can never be hidden with it
//   - the export is the rows on screen, in the order on screen, with the
//     columns on screen
//   - tiles (optional) count the rows before the tile filter, and "All"
//     clears the tile and the filter box together
//   - renderCap (optional, G360_K443) limits how many rows are DRAWN.
//     Filtering, sorting, tiles and the export still cover every row,
//     and the table says how many are not drawn. Omitted, nothing changes.
//   - pageSize (optional, G360_K677) draws the rows a page at a time with
//     Previous / Page N of M / Next and "showing a-b of N". Filtering,
//     sorting, tiles and the export still cover every matching row; a new
//     filter, sort or tile goes back to page 1. pageToKey (with rowKey)
//     turns to the page holding that row, so a row selected elsewhere (a
//     map, a list) is on screen. Omitted, nothing changes.

import { useEffect, useMemo, useState } from 'react';
import {
  useSort, sortRows, SortTh, TableFilter, TableToolbar,
  useHiddenCols, ColumnPicker, ColumnHideStyle, ExportCsv,
} from './table.jsx';
import { useTileFocus, tileFilter, FilterTiles, FilterBanner } from './tiles.jsx';

function valueOf(c, r) {
  if (typeof c.value === 'function') return c.value(r);
  return r == null ? null : r[c.key];
}
function blankToNull(v) {
  if (v === undefined || v === null) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  if (typeof v === 'number' && isNaN(v)) return null;
  return v;
}

export function DataTable({
  rows, columns, tableClass, storageKey, name = 'export',
  placeholder = 'Filter...', alwaysLabel, defaultHidden = [],
  tiles = null, rowKey, onRowClick, actions, rowStyle,
  emptyText = 'No row matches these filters.', exportLabel, toolbarExtra, card = true,
  renderCap, pageSize, pageToKey,
}) {
  const list = Array.isArray(rows) ? rows : [];
  const cols = useMemo(() => (columns || []).map((c, i) => ({ ...c, n: i + 1 })), [columns]);
  const hideable = cols.slice(1);
  const [q, setQ] = useState('');
  const { focus, tile, clear } = useTileFocus();
  const { sortKey, sortDir, toggleSort } = useSort();
  const { hidden, toggleCol, showAll } = useHiddenCols(storageKey, defaultHidden);

  const byKey = useMemo(() => {
    const m = {};
    cols.forEach((c) => { m[c.key] = c; });
    return m;
  }, [cols]);
  function sortVal(r, key) {
    const c = byKey[key];
    if (!c) return null;
    return blankToNull(typeof c.sort === 'function' ? c.sort(r) : valueOf(c, r));
  }

  const needle = q.trim().toLowerCase();
  const shown = useMemo(() => {
    let out = tiles ? tileFilter(list, tiles, focus) : list;
    if (needle) {
      out = out.filter((r) => cols.some((c) => {
        const v = typeof c.csv === 'function' ? c.csv(r) : valueOf(c, r);
        return String(v == null ? '' : v).toLowerCase().indexOf(needle) >= 0;
      }));
    }
    return sortRows(out, sortKey, sortDir, sortVal);
    // eslint-disable-next-line
  }, [list, tiles, focus, needle, sortKey, sortDir, cols]);

  const per = Number(pageSize) > 0 ? Math.floor(Number(pageSize)) : 0;
  const [page, setPage] = useState(1);
  const pages = per ? Math.max(1, Math.ceil(shown.length / per)) : 1;
  const cur = Math.min(Math.max(1, page), pages);
  // A new filter, sort or tile starts again at page 1.
  useEffect(() => { setPage(1); }, [needle, sortKey, sortDir, focus]);
  // Turn to the page that holds a row selected elsewhere.
  useEffect(() => {
    if (!per || pageToKey == null || !rowKey) return;
    const i = shown.findIndex((r, k) => rowKey(r, k) === pageToKey);
    if (i >= 0) setPage(Math.floor(i / per) + 1);
    // eslint-disable-next-line
  }, [pageToKey]);
  const paged = per ? shown.slice((cur - 1) * per, cur * per) : shown;
  const cap = !per && Number(renderCap) > 0 ? Number(renderCap) : 0;
  const drawn = cap && paged.length > cap ? paged.slice(0, cap) : paged;

  const visible = cols.filter((c) => !hidden.includes(c.key))
    .map((c) => ({ key: c.key, label: c.label, csv: (r) => (typeof c.csv === 'function' ? c.csv(r) : valueOf(c, r)) }));
  const span = cols.length + (actions ? 1 : 0);
  const cls = tableClass || 'g360-dt';
  function clearAll() { clear(); setQ(''); }

  const table = (
    <>
      <ColumnHideStyle tableClass={cls} columns={cols} hidden={hidden} />
      <table className={'tb ' + cls}>
        <thead>
          <tr>
            {cols.map((c) => (
              <SortTh key={c.key} k={c.key} label={c.label} title={c.title} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                      style={c.align === 'right' ? { textAlign: 'right' } : undefined} />
            ))}
            {actions ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr><td colSpan={span} style={{ textAlign: 'center', padding: 20, color: 'var(--g-text-2)', fontSize: 13 }}>{emptyText}</td></tr>
          ) : drawn.map((r, i) => (
            <tr key={rowKey ? rowKey(r, i) : i}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                style={Object.assign({}, onRowClick ? { cursor: 'pointer' } : {}, rowStyle ? rowStyle(r) || {} : {})}>
              {cols.map((c) => {
                const v = typeof c.render === 'function' ? c.render(r) : valueOf(c, r);
                return (
                  <td key={c.key} style={c.align === 'right' ? { textAlign: 'right' } : undefined}>
                    {v === null || v === undefined || v === '' ? <span style={{ color: 'var(--g-text-3)' }}>-</span> : v}
                  </td>
                );
              })}
              {actions ? <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>{actions(r)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  return (
    <div>
      {tiles ? (
        <>
          <FilterTiles tiles={tiles} rows={list} focus={focus} onTile={tile} onClear={clearAll} />
          <FilterBanner tiles={tiles} focus={focus} onClear={clear} />
        </>
      ) : null}
      <TableToolbar left={<TableFilter value={q} onChange={setQ} placeholder={placeholder} count={shown.length} />}>
        {toolbarExtra || null}
        {hideable.length > 0 ? (
          <ColumnPicker columns={hideable} hidden={hidden} onToggle={toggleCol} onShowAll={showAll}
                        alwaysLabel={alwaysLabel || ('The ' + String(cols[0] ? cols[0].label : 'first column').toLowerCase() + ' column is always shown.')} />
        ) : null}
        <ExportCsv rows={shown} columns={visible} name={name} label={exportLabel} />
      </TableToolbar>
      {card ? <div className="card pad-0" style={{ overflowX: 'auto' }}>{table}</div> : <div style={{ overflowX: 'auto' }}>{table}</div>}
      {per && shown.length > per ? (
        <div className="g360-dt-pager" data-testid="dt-pager" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10, fontSize: 12 }}>
          <button type="button" className="btn outline" data-testid="dt-prev" style={{ height: 28, fontSize: 12 }} disabled={cur <= 1} onClick={() => setPage(cur - 1)}>Previous</button>
          <span data-testid="dt-pageno" style={{ color: 'var(--g-text-2)' }}>Page {cur.toLocaleString()} of {pages.toLocaleString()}</span>
          <button type="button" className="btn outline" data-testid="dt-next" style={{ height: 28, fontSize: 12 }} disabled={cur >= pages} onClick={() => setPage(cur + 1)}>Next</button>
          <span data-testid="dt-range" style={{ color: 'var(--g-text-2)', marginLeft: 4 }}>
            showing {((cur - 1) * per + 1).toLocaleString()}-{Math.min(cur * per, shown.length).toLocaleString()} of {shown.length.toLocaleString()}; {per} a page. The export covers all {shown.length.toLocaleString()}.
          </span>
        </div>
      ) : null}
      {!per && drawn.length < shown.length ? (
        <p className="g360-dt-cap" style={{ fontSize: 12, color: 'var(--g-text-2)', margin: '8px 0 0' }}>
          Showing the first {drawn.length.toLocaleString()} of {shown.length.toLocaleString()} rows. Filter or sort to
          bring the others into view; the export covers all {shown.length.toLocaleString()}.
        </p>
      ) : null}
    </div>
  );
}

/* A thin bar for "share of the largest" or "share of the total" inside a
   cell. max <= 0 draws nothing: a bar with no scale is not a measurement. */
export function ShareBar({ value, max, color, title, width = 80 }) {
  const v = Number(value) || 0;
  const m = Number(max) || 0;
  if (m <= 0) return null;
  const pct = Math.max(0, Math.min(100, (100 * v) / m));
  return (
    <div title={title} style={{ height: 4, width, marginTop: 3, borderRadius: 2, background: 'var(--g-bg-soft)', display: 'inline-block' }}>
      <div style={{ height: 4, borderRadius: 2, width: pct + '%', background: color || 'var(--g-primary)' }} />
    </div>
  );
}
